import { LOYALTY_CREDIT_CARD_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import Image from 'components/Common/Image/Image'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import Caption from 'LuxKit/Typography/Caption'
import useLoyaltyCreditCardNewBadge from 'loyalty/hooks/useLoyaltyCreditCardNewBadge'
import useLoyaltyCreditCardPlacementTracking from 'loyalty/hooks/useLoyaltyCreditCardPlacementTracking'
import { useCallback } from 'react'

const SOCIETE_CREDIT_CARD_IMAGE_ID = 'societe-credit-card-nav-thumbnail'

/* Wraps the row purely so the impression observer has an element to watch. Kept as a plain block
   with no styling of its own, so the row's own layout inside the menu is unchanged. */
const ImpressionAnchor = styled.div`
  display: block;
`

/* Match the 64px Maldives row: 48px thumbnail plus 8px block padding. */
const LoyaltyCreditCardListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
  min-height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.L};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

/** Preserve the credit card artwork's aspect ratio instead of using a square thumbnail. */
const LoyaltyCreditCardImageContainer = styled.div`
  width: ${rem(48)};
  height: ${rem(32.5)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

/** Match the desktop badge with messaging tokens; LuxKit has no equivalent label variant. */
const NewBadge = styled.div`
  background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  padding: 0 ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props {
  navItem: App.NavItem
  onNavigate: () => void
}

function NavBarHamburgerMenuLoyaltyCreditCardBanner(props: Props) {
  const { navItem, onNavigate } = props
  const intl = useIntl()
  const { impressionRef, onPlacementClick } = useLoyaltyCreditCardPlacementTracking(LOYALTY_CREDIT_CARD_PLACEMENTS.hamburgerMenuItem)
  const showNewBadge = useLoyaltyCreditCardNewBadge()

  // Tracking sits alongside the menu's own navigation handling rather than replacing it, so
  // closing the menu still happens if the event ever fails to send.
  const onRowClick = useCallback(() => {
    onPlacementClick()
    onNavigate()
  }, [onPlacementClick, onNavigate])

  // Share the desktop navigation badge descriptor and translation.
  const newBadgeText = intl.formatMessage({
    id: '_2jYquwp3ORw1vKDAJ9x',
    defaultMessage: 'NEW',
  })

  return (
    /* ClickableListItem omits `ref` from its props, so the impression observer needs an element
       of its own. A plain wrapper keeps the row itself untouched. */
    <ImpressionAnchor ref={impressionRef}>
      <LoyaltyCreditCardListItem
        size="medium"
        startIcon={
          <LoyaltyCreditCardImageContainer>
            <Image
              id={SOCIETE_CREDIT_CARD_IMAGE_ID}
              dpr={2}
              fit="center"
              objectFit="cover"
            />
          </LoyaltyCreditCardImageContainer>
        }
        title={
          <TitleGroup>
            {showNewBadge && <NewBadge>
              <Caption variant="medium" weight="bold">{newBadgeText}</Caption>
            </NewBadge>}
            <span>{navItem.text}</span>
          </TitleGroup>
        }
        titleColour="normal-contrast"
        titleLineClamp={0}
        href={navItem.href}
        to={navItem.to}
        onClick={onRowClick}
        rel={navItem.target === '_blank' ? 'noopener noreferrer' : undefined}
        target={navItem.target}
        endIcon={<LineAngleRightBIcon />}
      />
    </ImpressionAnchor>
  )
}

export default NavBarHamburgerMenuLoyaltyCreditCardBanner
